/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import PublicSeoPage from '../../components/shared/PublicSeoPage';
import PageMeta from '../../components/shared/PageMeta';
import SeoConversionCTA from '../../components/shared/SeoConversionCTA';
import agents from '../../data/agents.json';

// The "get things done" surface, third of the three audience pages beside
// /developers (build with it) and /earn (get paid by it). This one is for the
// person who does not want to build or host anything and just wants a job done.
//
// GROUNDED, NOT INVENTED. Every vertical below is a real seed goal in HARTOS
// (integrations/agent_engine/goal_seeding.py, the bootstrap_p2p_* block) and
// the agent count comes from src/data/agents.json, the same registry
// /agents renders. Nothing here describes a capability that has no code behind
// it.
//
// The copy is deliberately careful about tense. These agents are seeded and
// running against real backends in some verticals and early in others, so the
// page says what the network is FOR and routes people to the live surfaces
// (/agents, /social, /download) rather than implying a finished marketplace
// with liquidity on both sides. Overclaiming here is the thing that costs
// trust, and it is the thing a launch audience checks first.
//
// Static content, so it prerenders cleanly. No em dashes in any rendered
// string.

// The twelve P2P service agents seeded in HARTOS goal_seeding.py. Titles and
// scope paraphrased from the seed descriptions, not embellished.
const VERTICALS = [
  { name: 'Local services',  what: 'Plumbing, electrical, cleaning. Providers register skills and availability, the agent classifies urgency and matches.', color: '#7c72ff' },
  { name: 'Freelance work',  what: 'Freelancers list skills, clients post jobs, direct matching with milestone based escrow.', color: '#7c72ff' },
  { name: 'Tutoring',        what: 'Teachers set their own rates. Free basic tutoring from the agent, escalating to a human when it should.', color: '#34d3ff' },
  { name: 'Rentals',         what: 'Cars, tools, cameras, spaces, equipment. Owner sets the rate, calendar based availability, deposit held in escrow.', color: '#34d3ff' },
  { name: 'Logistics',       what: 'Local bike couriers, city vans, intercity carriers, and a traveller network, with real time tracking.', color: '#17c07f' },
  { name: 'Rideshare',       what: 'Riders and drivers connect directly. Matching, tracking, settlement, SOS and chat.', color: '#17c07f' },
  { name: 'Marketplace',     what: 'Buy and sell anything. Listings, discovery, negotiation, escrow, delivery and reviews, with community governed disputes.', color: '#f0b23a' },
  { name: 'Grocery',         what: 'Community shoppers pick and deliver from local stores, with substitutions agreed in chat.', color: '#f0b23a' },
  { name: 'Food',            what: 'Restaurants and home cooks both list. Independent drivers, transparent pricing, no exclusive contracts.', color: '#f0b23a' },
  { name: 'Bills',           what: 'One gateway for electricity, water, gas, recharge, DTH, card and EMI, insurance and municipal tax.', color: '#ff8a80' },
  { name: 'Tickets',         what: 'Trains, buses, flights and events. Cross provider search, price comparison, PNR and waitlist tracking.', color: '#ff8a80' },
  { name: 'Health',          what: 'Doctor discovery, appointments, pharmacy price comparison, lab tests and wellness. It never diagnoses.', color: '#ff8a80' },
];

// How a job actually travels. Three steps because there are three, not because
// three is a tidy number for a landing page.
const FLOW = [
  {
    step: 'Ask',
    title: 'Say what you need, in your own words',
    body: 'No forms and no category tree to guess your way through. Ask from the app, from chat, or from your own machine. The agent works out what kind of job it is and what it still needs to know.',
  },
  {
    step: 'Match',
    title: 'It finds the person or the provider',
    body: 'Matching happens directly between the people involved rather than through a middleman taking a cut of each side. Where money moves, it moves through escrow and releases on milestones.',
  },
  {
    step: 'Done',
    title: 'You keep the record, and the relationship',
    body: 'The thread, the agreement and the outcome stay yours. Providers build a visible track record they own, which is what makes the next match better than the last.',
  },
];

export default function JobsPage() {
  // Read from the same registry /agents renders, so this number cannot drift
  // away from the page it points at.
  const agentCount = Array.isArray(agents) ? agents.length : 0;

  return (
    <>
      <PageMeta
        title="Get Things Done with AI Agents: Hire, Match and Settle | Hevolve"
        description="Put an AI agent on the job. Local services, freelance work, tutoring, rentals, logistics, deliveries, bills, tickets and health, matched directly between people with escrow where money moves. Runs on your own machine."
        ogTitle="Get things done with AI agents"
        ogDescription="Ask in your own words. The agent works out the job, finds the provider, and the record stays yours."
        path="/jobs"
      >
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Get things done with AI agents',
          url: 'https://hevolve.ai/jobs',
          description: 'Hire AI agents for real tasks: local services, freelance work, tutoring, rentals, logistics, bills, tickets and health. Direct matching between people, escrow where money moves.',
          publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' },
        })}</script>
      </PageMeta>

      <PublicSeoPage
        heading="Get things done"
        subheading="You do not have to build an agent or host one to use them. Say what you need and let the network find the person who can do it."
        maxWidth="lg"
      >
        <Grid container spacing={2.5} sx={{ mt: 1, mb: 5 }}>
          {FLOW.map((f, i) => (
            <Grid item xs={12} md={4} key={f.step}>
              <Box sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25,
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(124,114,255,0.28)',
                transition: 'transform .16s, border-color .2s',
                '&:hover': { transform: 'translateY(-3px)', borderColor: '#7c72ff' } }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: '#7c72ff' }}>
                  {f.step}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>{f.title}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7 }}>{f.body}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
          What the agents already cover
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2.5, maxWidth: 780 }}>
          Twelve service agents ship with the network. Each one runs as its own autonomous agent rather than as a
          menu item, which is why it can handle the parts of a job that do not fit a form.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {VERTICALS.map((v) => (
            <Grid item xs={12} sm={6} md={4} key={v.name}>
              <Box sx={{ height: '100%', p: 2.5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.1)', borderLeft: `3px solid ${v.color}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>{v.name}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.66)', mt: 0.5, lineHeight: 1.65 }}>{v.what}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(23,192,127,0.06)', border: '1px solid rgba(23,192,127,0.2)', mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>Why it is not another marketplace app</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7, maxWidth: 780 }}>
            The usual arrangement puts a company between the two people doing business and charges both of them for the
            privilege. Here the matching is peer to peer and the agent runs on your own hardware, so the conversation
            about your plumbing, your invoice or your prescription does not need to leave your machine to be useful.
            Where money moves it moves through escrow, and where a decision is medical the agent hands over to a human
            instead of guessing.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button component={Link} to="/agents" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#17c07f' } }}>
              {agentCount > 0 ? `Browse ${agentCount} agents` : 'Browse the agents'}
            </Button>
            <Button component={Link} to="/social" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#17c07f' } }}>See what the community is running</Button>
            <Button component={Link} to="/earn" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#17c07f' } }}>Provide a service instead</Button>
          </Stack>
        </Box>

        <SeoConversionCTA
          channel="jobs"
          headline="Put an agent on it"
          subhead="Install Nunba, ask for what you need in your own words, and let the agent do the finding. It runs on your machine, so there is no account to create before it is useful."
          primaryLabel="Get Nunba"
          primaryTo="/download"
          secondaryLabel="Browse the agents"
          secondaryTo="/agents"
        />
      </PublicSeoPage>
    </>
  );
}
